<template>
  <div class="search-results">
    <div class="scroll-wrapper">
      <div
        class="results-section"
        aria-live="polite"
        :aria-label="
          isLoading
            ? $t('search.loadingResultsAriaLabel')
            : !hasResults
              ? $t('search.noResultsAriaLabel', { searchTerm: route.query.q || '' })
              : $t('search.resultsCountAriaLabel', { count: results.length, searchTerm: route.query.q || '' })
        "
      >
        <AiAnswerCard
          v-if="showAiAnswer"
          :key="aiData.answer"
          :ai-data="aiData"
          @continue-in-chat="$emit('continueInChat')"
        />
        <template v-if="isLoading">
          <BaseCardSkeleton
            v-for="n in 6"
            :key="`skeleton-${n}`"
          />
        </template>
        <div
          v-else-if="!hasResults"
          class="no-results"
        >
          <span class="error-message">{{ $t('search.noResultsFor', { searchTerm: route.query.q }) }}</span>
          <span class="try-again-message">{{ $t('search.clearFiltersAndSearchAgain') }}</span>
        </div>
        <ResultsSection
          v-else
          :results="results"
          @result-clicked="$emit('result-clicked', $event)"
          @open-feedback-modal="$emit('openFeedbackModal')"
        />
        <template v-if="hasResults">
          <hr class="divider" />
          <div class="end-of-results">
            <span class="end-of-results-text">{{ $t('search.endOfResults') }}</span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import AiAnswerCard from './ResultCards/AiAnswerCard.vue';
import BaseCardSkeleton from './ResultCards/BaseCardSkeleton.vue';
import ResultsSection from './ResultsSection.vue';

const props = defineProps({
  results: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  aiData: {
    type: Object,
    default: () => ({
      answer: '',
      references: [],
      status: '',
      isLoading: false,
    }),
  },
  showAiAnswer: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['result-clicked', 'filterApplied', 'openFeedbackModal', 'continueInChat']);

const route = useRoute();

const hasResults = computed(() => props.results.length > 0);
</script>

<style lang="scss" scoped>
@use '../global/scss/mixins/_breakpoints.scss' as bp;

.search-results {
  --space-150: 6px;
  --space-200: 8px;
  --space-300: 12px;
  --space-400: 16px;
  --color-text-primary: #2d2d2d;
  --color-grey-75: #e9e9e9;
  --color-grey-100: #e0e0e0;
  --color-grey-200: #d3d3d3;
  --color-grey-300: #b3b3b3;
  --color-grey-400: #adadad;
  --color-blue-500: #1850c1;
  --z-index-search-filters: 1;

  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--primary-white);
  border-radius: 8px;
  height: auto;
  overflow: hidden;
}

.scroll-wrapper {
  padding: 16px;
  overflow-y: auto;
}

.no-results {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px;
  font-size: 14px;
  font-weight: 450;
  line-height: 16px;
  letter-spacing: 0.5px;
  color: #222;
  height: 240px;

  .error-message {
    color: var(--color-text-primary);
    font-size: 16px;
    font-weight: 700;
    line-height: 120%;
    letter-spacing: 0.8px;
  }
  .try-again-message {
    color: var(--color-text-primary);
    font-size: 16px;
    font-style: normal;
    font-weight: 450;
    line-height: 22px;
    letter-spacing: 0.6px;
  }
}

.results-section {
  gap: 12px;
  display: flex;
  flex-direction: column;
  padding-top: 8px;
}

.divider {
  background: var(--color-grey-400);
  height: 0.5px;
  width: 100%;
  margin-top: 12px;
  border: none;
}

.end-of-results {
  color: var(--color-text-tertiary, #949494);
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  margin: 16px;
}

@include bp.screen('<=md') {
  .search-results {
    height: calc(100vh - var(--search-bar-height, 74px));
  }
}

@include bp.screen('xs') {
  .search-results {
    border-radius: 0;
  }
}
</style>
